import { isEmpty } from 'lodash';
import { ChangeEvent, FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { ReactComponent as IconClose } from '../../../../assets/images/close.svg';
import { ProductInfoModel, RequestModel } from '../../../../models/request.model';
import { sendRequest } from '../../../../store/location';
import pushToDataLayer from '../../../../utils/pushToDataLayer';
import { VALIDATION_TEST } from '../../../../utils/validationTests';
import Button, { ButtonKind } from '../button/Button';
import { Input, ValidationTestType } from '../input';

const RequestForm: FC<{ close: () => void; data: ProductInfoModel[]; dataLayerEventName: string }> = ({
    close,
    data,
    dataLayerEventName,
}) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const [formValues, setFormValues] = useState<RequestModel>({
        company: '',
        name: '',
        email: '',
        city: '',
        zipcode: '',
        message: '',
        phone: '',
        producturns: !isEmpty(data) ? data.map(({ urn }) => urn) : [],
    });
    const [errors, setErrors] = useState<{ [key: string]: keyof typeof ValidationTestType | null }>({
        name: null,
        email: null,
        city: null,
        zipcode: null,
        message: null,
    });

    const validationInput = (
        input: 'name' | 'email' | 'city' | 'zipcode' | 'message',
        validationTests: ValidationTestType[] = [ValidationTestType.hasValue]
    ): void => {
        const res: ValidationTestType[] | null = validationTests.filter((test: keyof typeof ValidationTestType) =>
            !VALIDATION_TEST[test](formValues[input] as string | number) ? test : null
        );

        setErrors({ ...errors, [input]: res[0] || null });
    };

    const updateValue =
        (key: keyof RequestModel) =>
        ({ target }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void =>
            setFormValues({ ...formValues, [key]: target.value });

    const isDisabled = (): boolean =>
        Object.values(errors).some(err => err !== null) ||
        !formValues.city ||
        !formValues.name ||
        !formValues.zipcode ||
        !formValues.email ||
        !formValues.message ||
        isEmpty(formValues.producturns);

    return (
        <div className="request-form__wrapper">
            <div className="request-form__header">
                {intl.formatMessage({ id: 'modals.titleRequest', defaultMessage: 'Anfrage senden' })}

                <Button kind={ButtonKind.LINK} onClick={close}>
                    <IconClose stroke="white" width="14" height="14" />
                </Button>
            </div>

            <div className="request-form__body">
                <div className={'products-wrapper'}>
                    <h5>{intl.formatMessage({ id: 'modals.productsTitle', defaultMessage: 'Produkte:' })}</h5>

                    {!isEmpty(data) &&
                        data.map(({ format, id, address }) => (
                            <div className={'product-row'} key={id}>
                                {`${format ? format.replace('<br>', ' ') : ''} - ${
                                    address ? address.replace('<br>', ' ') : ''
                                } (ID: ${id})`}
                            </div>
                        ))}
                </div>

                <Input
                    name={'name'}
                    placeholder={intl.formatMessage({ id: 'modals.requestForm.name', defaultMessage: 'Name *' })}
                    value={formValues['name']}
                    onChange={updateValue('name')}
                    classWrapper="input-width-50"
                    errorType={errors.name}
                    onBlur={() => validationInput('name')}
                />

                <Input
                    name={'company'}
                    placeholder={intl.formatMessage({ id: 'modals.requestForm.company', defaultMessage: 'Firma/Verein' })}
                    value={formValues['company']}
                    onChange={updateValue('company')}
                    classWrapper="input-width-50"
                />

                <Input
                    name={'email'}
                    placeholder={intl.formatMessage({ id: 'modals.requestForm.email', defaultMessage: 'E-Mail *' })}
                    value={formValues['email']}
                    onChange={updateValue('email')}
                    classWrapper="input-width-50"
                    errorType={errors.email}
                    onBlur={() => validationInput('email', [ValidationTestType.isEmail])}
                />

                <Input
                    name={'phone'}
                    placeholder={intl.formatMessage({ id: 'modals.requestForm.phone', defaultMessage: 'Telefon' })}
                    value={formValues['phone']}
                    onChange={updateValue('phone')}
                    classWrapper="input-width-50"
                />

                <Input
                    name={'city'}
                    placeholder={intl.formatMessage({ id: 'modals.requestForm.city', defaultMessage: 'Ort *' })}
                    value={formValues['city']}
                    onChange={updateValue('city')}
                    errorType={errors.city}
                    onBlur={() => validationInput('city')}
                    classWrapper="input-width-50"
                />

                <Input
                    name={'zipcode'}
                    placeholder={intl.formatMessage({ id: 'modals.requestForm.zipcode', defaultMessage: 'PLZ *' })}
                    value={formValues['zipcode']}
                    onChange={updateValue('zipcode')}
                    errorType={errors.zipcode}
                    onBlur={() => validationInput('zipcode', [ValidationTestType.isNumber, ValidationTestType.hasValue])}
                    classWrapper="input-width-50"
                />

                <Input
                    type={'textarea'}
                    name={'message'}
                    placeholder={intl.formatMessage({
                        id: 'modals.requestForm.message',
                        defaultMessage: 'Ihre Nachricht *',
                    })}
                    value={formValues['message']}
                    errorType={errors.message}
                    onBlur={() => validationInput('message')}
                    onChange={updateValue('message')}
                    classWrapper="input-width-full"
                />

                <Button
                    disabled={isDisabled()}
                    onClick={() => {
                        close();
                        pushToDataLayer(dataLayerEventName);
                        dispatch(sendRequest(formValues));
                    }}
                >
                    {intl.formatMessage({ id: 'modals.requestForm.submitBtn', defaultMessage: 'Senden' })}
                </Button>
            </div>
        </div>
    );
};

export default RequestForm;
